/**
 * Clinicians Tab - Assign and remove clinicians from a client
 */

import { useState } from 'react';
import styled from 'styled-components';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useClientClinicians, useRemoveClinician } from '../../hooks/useClients';
import { AssignProvidersPanel } from '../../components/clients/AssignProvidersPanel';
import type { ClientClinician } from '../../types/clients';

interface CliniciansTabProps {
  clientId: string;
  /** When false (default), assign/remove clinician actions are hidden — mirrors the drawer's guard. */
  canManage?: boolean;
  /** User id of the client's Overseeing BCBA — may or may not be on the roster below (see red-dot.service.ts getAssignableUsers). */
  supervisingClinicianId?: string | null;
  /** The Overseeing BCBA's name, for the case where they aren't on the roster and so have no ClientClinician row of their own. */
  supervisingClinician?: { id: string; firstName: string; lastName: string } | null;
}

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const TabHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TabTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TableWrapper = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.backgrounds.gray};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  white-space: nowrap;
`;

const ThActions = styled(Th)`
  width: 60px;
`;

const Tr = styled.tr<{ $offRoster?: boolean }>`
  background: ${({ theme, $offRoster }) => ($offRoster ? theme.colors.backgrounds.light : 'transparent')};

  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
  }
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Td = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  vertical-align: middle;
`;

const SecondaryText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const IconButton = styled.button<{ $variant?: 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme, $variant }) =>
    $variant === 'danger' ? theme.colors.status.error.text : theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme, $variant }) =>
      $variant === 'danger' ? theme.colors.status.error.bg : theme.colors.backgrounds.gray};
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[10]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const SkeletonBar = styled.div<{ $width?: string }>`
  height: 14px;
  width: ${({ $width }) => $width ?? '100%'};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

// ==================== Helpers ====================

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

// ==================== CliniciansTab ====================

export const CliniciansTab = ({
  clientId,
  canManage = false,
  supervisingClinicianId = null,
  supervisingClinician = null,
}: CliniciansTabProps) => {
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const { data: clinicians, isLoading } = useClientClinicians(clientId);
  const removeClinician = useRemoveClinician(clientId);

  const handleRemove = (memberId: string) => {
    setRemovingId(memberId);
    removeClinician.mutate(memberId, {
      onSettled: () => setRemovingId(null),
    });
  };

  // The Overseeing BCBA is not always on the roster (see red-dot.service.ts
  // getAssignableUsers) — when they aren't, synthesize a row for them so the
  // section doesn't read as if no one is assigned.
  const bcbaOffRoster =
    supervisingClinicianId && !clinicians?.some((c) => c.userId === supervisingClinicianId)
      ? supervisingClinician
      : null;
  const isEmpty = (!clinicians || clinicians.length === 0) && !bcbaOffRoster;

  return (
    <Container>
      <TabHeader>
        <TabTitle>Assigned Clinicians</TabTitle>
        {canManage && (
          <Button
            variant="primary-blue"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={() => setShowAssignModal(true)}
          >
            Assign Clinician
          </Button>
        )}
      </TabHeader>

      <TableWrapper>
        <Table>
          <Thead>
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Assigned Date</Th>
              {canManage && <ThActions />}
            </tr>
          </Thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  <Td>
                    <SkeletonBar $width="140px" />
                  </Td>
                  <Td>
                    <SkeletonBar $width="180px" />
                  </Td>
                  <Td>
                    <SkeletonBar $width="100px" />
                  </Td>
                  {canManage && <Td />}
                </tr>
              ))
            ) : isEmpty ? (
              <tr>
                <td colSpan={canManage ? 4 : 3}>
                  <EmptyState>No clinicians assigned to this client yet.</EmptyState>
                </td>
              </tr>
            ) : (
              <>
                {bcbaOffRoster && (
                  <Tr $offRoster>
                    <Td>
                      <NameCell>
                        {bcbaOffRoster.firstName} {bcbaOffRoster.lastName}
                        <Badge variant="info">Overseeing BCBA</Badge>
                      </NameCell>
                    </Td>
                    <Td>
                      <SecondaryText>Not on roster</SecondaryText>
                    </Td>
                    <Td>
                      <SecondaryText>—</SecondaryText>
                    </Td>
                    {canManage && <Td />}
                  </Tr>
                )}
                {clinicians?.map((c: ClientClinician) => {
                  const isSupervisingBcba = c.userId === supervisingClinicianId;
                  return (
                    <Tr key={c.memberId}>
                      <Td>
                        <NameCell>
                          {c.firstName} {c.lastName}
                          {isSupervisingBcba && <Badge variant="info">Overseeing BCBA</Badge>}
                        </NameCell>
                      </Td>
                      <Td>
                        <SecondaryText>{c.email}</SecondaryText>
                      </Td>
                      <Td>
                        <SecondaryText>{formatDate(c.assignedAt)}</SecondaryText>
                      </Td>
                      {canManage && (
                        <Td>
                          {/* Removing the Overseeing BCBA goes through the Set/Replace BCBA flow, not roster removal. */}
                          {!isSupervisingBcba && (
                            <IconButton
                              $variant="danger"
                              title="Remove clinician"
                              aria-label={`Remove ${c.firstName} ${c.lastName}`}
                              onClick={() => handleRemove(c.memberId)}
                              disabled={removingId === c.memberId}
                            >
                              <Trash2 />
                            </IconButton>
                          )}
                        </Td>
                      )}
                    </Tr>
                  );
                })}
              </>
            )}
          </tbody>
        </Table>
      </TableWrapper>

      {showAssignModal && (
        <AssignProvidersPanel clientId={clientId} onClose={() => setShowAssignModal(false)} />
      )}
    </Container>
  );
};
